import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { useNavigate, useParams } from "react-router-dom";
import {
  getRequiredFields,
  removeEmptyFields,
  setEmptyFieldsToNull,
} from "@/lib/utils";
import { DESCRIPTION, INVENTORY_NAMES, NAME, KIT, KIT_TYPE} from "../consts";
import { routes } from "@/routes/utils";
import { useIsMobile } from "@/hooks/use-mobile";
import { formValues, schema } from "./validations";
import { useAddTlmMasterMutation } from "@/services/masterData";
import { toast } from "sonner";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import useGetTlmMasterDetailQuery from "@/services/masterData/tlm-master/useTlmMasterDetailMutation";
import useUpdateTlmMastersMutation from "@/services/masterData/tlm-master/useUpdateTlmMastersMutation";
import useGetInventorySetupDetailQuery from "@/services/masterData/inventorysetup/useInventorySetupDetailMutation";
import { useGetActiveInventorySetup } from "@/services/masterData/inventorysetup/useGetActiveInventorySetup";

const useAddEdit = () => {
  const { action, id } = useParams();
  const navigate = useNavigate();
  const isView = action == routes.view;
  const isEdit = action == routes.edit;
  const isMobile = useIsMobile();
  const queryClient = useQueryClient();

  const form = useForm<formValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      [NAME]: "",
      // [INVENTORY_NAMES]: [],
      [DESCRIPTION]: "",
    },
  });

  const requiredFields = getRequiredFields(schema);

  const { data: detailRes, isPending: isDetailLoading } =
    useGetTlmMasterDetailQuery(isEdit || isView ? String(id) : undefined);

  const { data: inventoryNamesData, isPending: isDetailLoading1 } =
    useGetActiveInventorySetup();
  const inventoryOptions = (inventoryNamesData?.data || []).map((m: any) => ({
    label: m?.name,
    value: String(m?._id),
  }));

  const { mutateAsync: updateTlmMaster, isPending: isUpdating } =
    useUpdateTlmMastersMutation({
      onSuccess: () => {
        toast.success("KIT Master updated successfully");
        onBackClick();
      },
      onError: (error: any) => {
        toast.error(
          error.response.data?.issues?.message ||
            error.response.data.message ||
            "Failed to update tlm master"
        );
      },
    });

  const { mutateAsync: addTlm, isPending } = useAddTlmMasterMutation({
    onSuccess: () => {
      toast.success("TLM created successfully");
      onBackClick();
    },
    onError: (error: any) => {
      toast.error(
        typeof error?.response?.data?.message === "string"
          ? error.response.data.message
          : "Failed to create TLM"
      );
    },
  });

  useEffect(() => {
  if ((isEdit || isView) && detailRes?.data) {
    const d = detailRes.data;
    form.reset({
      [NAME]: String(d?.name ?? ""),
      [DESCRIPTION]: d?.description ? String(d?.description) : "",
      // [INVENTORY_NAMES]: d?.inventoryNames
      //   ? d?.inventoryNames.map((item: any) => String(item._id)) // ✅ Fix: use item._id
      //   : [],
      // [KIT]: d?.kitId ? String(d?.kitId._id) : "", // ✅ Fix: use String(d?.kitId._id) for single select
      [KIT_TYPE]: d?.kitType ? String(d?.kitType) : ""
    });
  }
}, [detailRes, isEdit, isView, form]);

  const onBackClick = () => {
    navigate(`/${routes.admin}/${routes.masterData}`);
  };

  const onSubmit = async (data: formValues) => {
    let _data = removeEmptyFields(data);
    _data = setEmptyFieldsToNull(_data);

    if (isEdit && id) {
      await updateTlmMaster({ id: String(id), data: _data });
      queryClient.invalidateQueries({ queryKey: ["tlmMaster"] });
      return;
    }
    await addTlm({ data: _data });
    queryClient.invalidateQueries({ queryKey: ["tlmMaster"] });
  };

  return {
    form,
    onSubmit,
    requiredFields,
    isView,
    onCancel: onBackClick,
    isMobile,
    action,
    isEdit,
    isLoading: isPending,
    inventoryNamesData: inventoryOptions,
  };
};

export default useAddEdit;
